import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { jwtDecode } from "jwt-decode";
import {
  AccessPayload,
  AuthResponse,
  LoginStage,
} from "../../common/types/types";
import { getApi } from "../../api/api";
import { selectCurrentCluster } from "../../store/slices/currentClusterSlice";
import { selectCurrentUser } from "../../store/slices/currentUserSlice";
import { selectAuthToken } from "../../store/slices/authSlice";
import { delayAction } from "../../common/utils/functions/delayAction";
import { ROUTES } from "../../common/utils/constants";
import { getFromStorage } from "../../common/utils/functions/localStorage";
import { openCartModal } from "../../store/slices/commonModalSlice";

declare global {
  interface Window {
    Telegram?: {
      WebApp?: {
        initData: string;
        initDataUnsafe: {
          user?: {
            id: number;
            first_name?: string;
            username?: string;
            photo_url?: string;
          };
          auth_date?: number;
          hash?: string;
        };
      };
    };
  }
}

export const useTelegramLogin = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [stage, setStage] = useState<LoginStage>(LoginStage.botLink);
  const [message, setMessage] = useState("");
  const [isLogin, setIsLogin] = useState(true);
  const [legalityChecked, setLegalityChecked] = useState(false);

  const api = getApi();
  const currentCluster = useSelector(selectCurrentCluster);
  const currentUser = useSelector(selectCurrentUser);
  const accessToken = useSelector(selectAuthToken);

  // Данные из URL
  const urlParams = Object.fromEntries(searchParams.entries());

  // Определяем Mini App
  const isMiniApp =
    typeof window !== "undefined" &&
    !!window.Telegram?.WebApp?.initData &&
    window.Telegram?.WebApp?.initData.length > 0;

  const initData = window.Telegram?.WebApp?.initData;

  // Безопасное получение telegramId
  const getTelegramId = useCallback((): string | null => {
    if (isMiniApp && initData) {
      try {
        const params = new URLSearchParams(initData);
        const userStr = params.get("user");
        if (userStr) {
          const user = JSON.parse(userStr);
          return user.id?.toString() || null;
        }
      } catch (error) {
        console.warn("Failed to parse initData user", error);
      }
      return null;
    }
    return urlParams.id || null;
  }, [isMiniApp, initData, urlParams.id]);

  const telegramId = getTelegramId();

  const [loading, setLoading] = useState(() => !!telegramId && !currentUser);

  useEffect(() => {
    if (!accessToken) return;
  }, [accessToken, currentUser?.id]);

  const botLoginAction = useCallback(async () => {
    try {
      setLoading(true);
      setStage(LoginStage.botLoginProcess);
      setMessage("Идет авторизация, пожалуйста подождите");

      await delayAction(2000);

      let result: AuthResponse;
      const basePayload = {
        clusterId: currentCluster?.id,
      };

      if (isMiniApp && initData) {
        const payload = {
          ...basePayload,
          initData,
        };

        if (isLogin) {
          result = await api.miniAppLogin(payload);
        } else {
          result = await api.miniAppRegister(payload);
        }
      } else {
        const payload = {
          ...basePayload,
          ...urlParams,
        };

        if (isLogin) {
          result = await api.botLogin(payload);
        } else {
          result = await api.botRegister(payload);
        }
      }

      if (result) {
        if (result.registerAction) {
          setMessage(
            "Мы не нашли ваш аккаунт в нашей системе. После нажатия кнопки будет выполнена регистрация. Пожалуйста, подтвердите согласие с документами ниже",
          );
          setIsLogin(false);
          setLoading(false);
          return;
        } else if (result.dateNotValid) {
          setStage(LoginStage.botLoginError);
          setMessage(
            "Данные устарели, перейдите в бот, для повторной авторизации",
          );
          setLoading(false);

          await delayAction(3000);
          navigate(ROUTES.MAIN);
          return;
        } else if (result.accessToken) {
          setSearchParams({});
          setMessage(isLogin ? "Вы авторизованы" : "Вы зарегистрированы");
          await delayAction(2000);

          const afterAction = getFromStorage("afterAction");
          const decoded = jwtDecode<AccessPayload>(result.accessToken);
          const hasEmail = !!decoded.email;
          const shouldCollectEmail = currentCluster?.collectEmail && !hasEmail;

          // 1. Корзина (высший приоритет)
          if (afterAction === "cart") {
            navigate(ROUTES.MAIN, { replace: true });
            setTimeout(() => {
              dispatch(openCartModal());
            }, 500);
            setLoading(false);
            return;
          }

          // 2. Сбор email
          if (shouldCollectEmail && !hasEmail) {
            navigate(`${ROUTES.EMAIL_LOGIN}?attach=true`);
            return;
          }

          // 3. Обычный вход на главную
          navigate(ROUTES.MAIN, { replace: true });
          setLoading(false);
          return;
        }
      }
    } catch (error: any) {
      setSearchParams({});
      if (error.response?.status === 401) {
        setMessage(
          "Возникла ошибка авторизации, вернитесь в бот, и повторите вход",
        );
      } else {
        setMessage("Возникла ошибка авторизации обратитесь в поддержку");
      }

      setStage(LoginStage.botLoginError);
      setLoading(false);

      await delayAction(2000);
      navigate(ROUTES.MAIN);
    }
  }, [
    isMiniApp,
    initData,
    urlParams,
    isLogin,
    currentCluster,
    api,
    navigate,
    dispatch,
    setSearchParams,
  ]);

  const toggleLegality = useCallback(() => {
    setLegalityChecked((prev) => !prev);
  }, []);

  // Редирект если уже авторизован (не через авторизацию, а просто зашел)
  useEffect(() => {
    if (currentUser) {
      navigate(ROUTES.MAIN, { replace: true });
    }
  }, [currentUser, navigate]);

  // Запуск авторизации если есть параметры и нет пользователя
  useEffect(() => {
    if (telegramId && !currentUser) {
      botLoginAction();
    }
  }, [telegramId, currentUser, botLoginAction]);

  return {
    stage,
    loading,
    message,
    isLogin,
    toggleLegality,
    legalityChecked,
    botLoginAction,
    currentUser
  };
};
